import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Search, Building2, MapPin, Landmark, ShieldCheck, ArrowRight } from "lucide-react";
import { TIER_STYLE, CATEGORY_LABEL, TierBadge, ReputationBadge } from "@/components/network/TierBadge";

export default function TrustNetwork() {
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({ tiers: [], categories: [] });
  const [tier, setTier] = useState("all");
  const [category, setCategory] = useState("all");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = { status: "verified" };
      if (tier !== "all") params.tier = tier;
      if (category !== "all") params.category = category;
      if (q) params.q = q;
      const [dir, tiers, cats] = await Promise.all([
        api.get("/network/directory", { params }),
        api.get("/network/tiers"),
        api.get("/network/categories"),
      ]);
      setItems(dir.data);
      setMeta({ tiers: tiers.data.tiers, categories: cats.data.categories });
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tier, category]);

  return (
    <div>
      <Navbar />
      {/* Hero */}
      <section className="relative bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 grid-lines opacity-10" />
        <div className="max-w-[1400px] mx-auto px-6 py-16 grid md:grid-cols-[1.5fr_1fr] gap-10 relative">
          <div>
            <div className="kicker text-primary-foreground/80">THOM Verify™ · Trust Network</div>
            <h1 className="display text-4xl sm:text-5xl lg:text-6xl mt-2 leading-[0.95]">
              A continental network of<br/>certified organisations.
            </h1>
            <p className="mt-5 text-primary-foreground/90 max-w-xl text-base">
              Every organisation in the THOM Verify Trust Network™ has been vetted, tiered and given
              scoped permission to contribute lifecycle data to Africa's most trusted asset registry.
              Choose from a directory you can rely on — not advertisements.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-2">
              <Link to="/network/register" className="w-full sm:w-auto"><Button size="lg" variant="secondary" className="w-full sm:w-auto" data-testid="btn-network-register">Register your organisation</Button></Link>
              <Link to="/network/partner" className="w-full sm:w-auto"><Button size="lg" variant="outline" className="w-full sm:w-auto border-primary-foreground/40 text-primary-foreground hover:bg-primary-foreground/10" data-testid="btn-partner-portal">Partner portal</Button></Link>
            </div>
          </div>
          <div className="border border-primary-foreground/20 bg-primary-foreground/5 p-6 rounded-sm">
            <div className="kicker text-primary-foreground/70">Partner Tiers</div>
            <div className="mt-3 space-y-2">
              {["bronze","silver","gold","platinum","government"].map(t => (
                <div key={t} className="flex items-center gap-3 text-sm flex-wrap">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: TIER_STYLE[t].ring }} />
                  <span className="mono uppercase tracking-widest text-[11px]">{TIER_STYLE[t].label}</span>
                  <span className="text-primary-foreground/70 text-[11px] sm:ml-auto">accredited &amp; audited</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-border bg-card">
        <div className="max-w-[1400px] mx-auto px-6 py-4 flex flex-wrap gap-3 items-center">
          <div className="flex items-center gap-2 flex-1 min-w-[260px]">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              placeholder="Search verified organisations…"
              className="max-w-md"
              data-testid="network-search-input"
            />
          </div>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger className="w-[220px]" data-testid="network-filter-category"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {meta.categories.map(c => <SelectItem key={c} value={c}>{CATEGORY_LABEL[c] || c}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={tier} onValueChange={setTier}>
            <SelectTrigger className="w-[180px]" data-testid="network-filter-tier"><SelectValue placeholder="All tiers" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All tiers</SelectItem>
              {meta.tiers.map(t => <SelectItem key={t} value={t}>{TIER_STYLE[t]?.label || t}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} data-testid="network-apply-filters">Apply</Button>
        </div>
      </section>

      {/* Directory grid */}
      <section className="max-w-[1400px] mx-auto px-6 py-10">
        {loading ? (
          <div className="mono text-sm text-muted-foreground">Loading directory…</div>
        ) : items.length === 0 ? (
          <div className="border border-border p-10 text-center bg-card">
            <div className="kicker">No results</div>
            <div className="display text-2xl mt-1">No organisations match your filters</div>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(o => <OrgCard key={o.id} org={o} />)}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
}

const OrgCard = ({ org }) => {
  const s = TIER_STYLE[org.tier] || TIER_STYLE.bronze;
  return (
    <Link
      to={`/network/${org.id}`}
      data-testid={`org-card-${org.id}`}
      className="group border border-border bg-card p-5 rounded-sm hover:border-primary/60 transition-colors flex flex-col gap-3"
    >
      <div className="flex items-start gap-3">
        <div className="h-14 w-14 rounded-sm grid place-items-center border" style={{ background: `${s.ring}18`, borderColor: `${s.ring}66` }}>
          {org.category === "government" ? <Landmark className="h-6 w-6" style={{ color: s.ring }} /> : <Building2 className="h-6 w-6" style={{ color: s.ring }} />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 text-[10px] mono uppercase tracking-widest text-muted-foreground">
            <span>{CATEGORY_LABEL[org.category] || org.category}</span>
            {org.status === "verified" && <ShieldCheck className="h-3 w-3 text-primary" />}
          </div>
          <div className="display text-lg leading-tight truncate">{org.name}</div>
          <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-1">
            <MapPin className="h-3 w-3" /> {org.city || org.country}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <TierBadge tier={org.tier} />
        <ReputationBadge rep={org.reputation} />
      </div>
      {org.description && (
        <p className="text-sm text-muted-foreground line-clamp-2">{org.description}</p>
      )}
      <div className="mt-auto flex items-center justify-between pt-3 border-t border-border/60">
        <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">
          {org.reputation?.signals?.accepted_updates || 0} trusted updates
        </div>
        <span className="text-primary text-xs font-semibold flex items-center gap-1 group-hover:gap-2 transition-all">
          View profile <ArrowRight className="h-3 w-3" />
        </span>
      </div>
    </Link>
  );
};
